import { skipToken } from "@reduxjs/toolkit/query/react";
import { useTenantTranslation } from "hooks/formatters";
import { useDefaultMaterialLimits } from "hooks/useDefaultMaterialLimits";
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import {
  useGetMaterialMetadataSetQuery,
  useGetMaterialPhysicsSetQuery,
  useListMaterialConstraintClassesQuery,
} from "src/store/api/generatedApi";
import {
  deriveLimitColumns,
  buildLimitLabelsBySignature,
} from "./materialLimitMatrix";

/** Maps each material-limit coefficient signature to its translated display label. */
export const useLimitLabelBySignature = (): Record<string, string> => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const { activeSet } = useDefaultMaterialLimits();
  const { data: materialMetadataSet } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });
  const { data: materialPhysicsSet } = useGetMaterialPhysicsSetQuery({
    tenantName,
    materialPhysicsSetId: "default",
  });
  const { data: constraintClasses } = useListMaterialConstraintClassesQuery(
    materialMetadataSet && materialPhysicsSet
      ? {
          tenantName,
          materialMetadataSetId: materialMetadataSet.id,
          materialPhysicsSetId: materialPhysicsSet.id,
        }
      : skipToken
  );
  const classNameById = useMemo(
    () =>
      (constraintClasses ?? []).reduce<Record<number, string>>(
        (names, constraintClass) => {
          names[constraintClass.id] = constraintClass.name;
          return names;
        },
        {}
      ),
    [constraintClasses]
  );
  const columns = useMemo(
    () => deriveLimitColumns(activeSet?.material_limits ?? []),
    [activeSet]
  );
  return useMemo(
    () => buildLimitLabelsBySignature(columns, classNameById, t),
    [columns, classNameById, t]
  );
};
